import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import BottomNavigation from '../../components/BottomNavigation/BottomNavigation';
import { FaCube, FaHashtag, FaExchangeAlt } from 'react-icons/fa';
import AssetIcon from '../../components/AssetIcon';
import Toast from '../../components/Toast';
import { formatAsset, formatCurrency } from '../../utils/format';
import './Swap.css';
import { useUser } from '../../context/UserContext';
import { swapService } from '../../services/swapService';
import { miningAssetService } from '../../services/miningAssetService';
import { assemblyLineService } from '../../services/assemblyLineService';

const Swap = ({ onTabChange }) => {
  const { t } = useTranslation();
  const { walletAddress } = useUser();
  const [records, setRecords] = useState([]);
  const [assets, setAssets] = useState([]);
  const [myAssetBalances, setMyAssetBalances] = useState({}); // 用户持有的资产余额
  const [loading, setLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState('');
  const [quantity, setQuantity] = useState('');

  useEffect(() => {
    if (walletAddress) {
      loadSwapRecords();
      loadMyAssetBalances();
    }
    loadMiningAssets();
  }, [walletAddress]);

  const loadSwapRecords = async () => {
    if (!walletAddress) return;
    
    try {
      const data = await swapService.getUserSwaps(walletAddress, 1, 20);
      if (data && data.records) {
        setRecords(data.records);
      }
    } catch (error) {
      console.error('加载交换记录失败:', error);
    }
  };

  const loadMiningAssets = async () => {
    try {
      const data = await miningAssetService.getTodayAssets();
      if (data) {
        setAssets(data);
        if (data.length > 0) {
          setSelectedAsset(data[0].name);
        }
      }
    } catch (error) {
      console.error('加载挖矿资产失败:', error);
    }
  };

  // 加载用户持有的资产余额（统一统计逻辑，和后端保持一致）
  const loadMyAssetBalances = async () => {
    if (!walletAddress) return;
    
    try {
      const assemblyLines = await assemblyLineService.getUserAssemblyLines(walletAddress);
      if (!assemblyLines) return;
      
      // 统计每种挖矿资产的余额（统计所有相关记录，包括增加和扣除）
      const balances = {};
      assemblyLines.forEach(line => {
        // 统计所有挖矿资产类型（不包括USDT和ETH）
        if (line.assetType && line.assetType !== 'USDT' && line.assetType !== 'ETH') {
          const assetName = line.assetType;
          if (!balances[assetName]) {
            balances[assetName] = 0;
          }
          // 累加所有数量（正数表示增加，负数表示扣除）
          balances[assetName] += parseFloat(line.quantity || 0);
        }
      });
      
      // 确保余额不为负数
      Object.keys(balances).forEach(key => {
        if (balances[key] < 0) {
          balances[key] = 0;
        }
      });
      
      setMyAssetBalances(balances);
    } catch (error) {
      console.error('加载资产余额失败:', error);
    }
  };

  const handleSwap = async () => {
    if (!walletAddress) {
      Toast.warning('请先连接钱包');
      return;
    }

    if (!selectedAsset || !quantity || parseFloat(quantity) <= 0) {
      Toast.warning('请选择资产并输入有效的数量');
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      Toast.error('请输入有效的数量');
      return;
    }

    // 前端验证：检查余额
    const balance = myAssetBalances[selectedAsset] || 0;
    if (balance < quantityNum) {
      Toast.error(`资产余额不足！\n当前持有: ${formatAsset(balance)} ${selectedAsset}\n申请兑换: ${formatAsset(quantityNum)} ${selectedAsset}`);
      return;
    }

    try {
      setLoading(true);
      const result = await swapService.createSwap(walletAddress, selectedAsset, quantityNum);
      
      // 显示兑换详情
      const targetAmount = result.targetAmount || (quantityNum * parseFloat(assets.find(a => a.name === selectedAsset)?.price || 0));
      Toast.success(`交换申请成功！\n${formatAsset(quantityNum)} ${selectedAsset} → ${formatCurrency(targetAmount)} USDT\n状态：待处理`);
      
      setQuantity('');
      loadSwapRecords();
      loadMyAssetBalances(); // 刷新余额
    } catch (error) {
      const errorMsg = error.message || '未知错误';
      if (errorMsg.includes('余额不足')) {
        Toast.error(`交换失败：${errorMsg}\n\n请检查您的资产余额。`);
      } else if (errorMsg.includes('登录已过期')) {
        Toast.error(errorMsg);
      } else {
        Toast.error('交换失败: ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="swap-page">
      <Header />
      <div className="swap-content">
        {/* 交换表单 */}
        {walletAddress && (
          <div className="swap-card" style={{ marginBottom: '20px' }}>
            <div className="swap-field">
              <label className="swap-label">{t('swap.selectAsset')}</label>
              <select 
                className="swap-select"
                value={selectedAsset}
                onChange={(e) => setSelectedAsset(e.target.value)}
              >
                {assets.map(asset => {
                  const balance = myAssetBalances[asset.name] || 0;
                  return (
                    <option key={asset.id || asset.assetId} value={asset.name}>
                      {asset.name} {balance > 0 ? `(${t('swap.holding')}: ${balance.toFixed(8)})` : `(${t('swap.balance')}: 0)`}
                    </option>
                  );
                })}
              </select>
              {selectedAsset && (
                <div className="swap-subtext" style={{ marginTop: '4px' }}>
                  {t('swap.currentBalance')}: {myAssetBalances[selectedAsset] ? myAssetBalances[selectedAsset].toFixed(8) : '0.00000000'} {selectedAsset}
                </div>
              )}
            </div>
            <div className="swap-field">
              <label className="swap-label">{t('swap.quantity')}</label>
              <input
                type="number"
                className="swap-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder={t('swap.enterQuantity')}
                min="0"
                max={myAssetBalances[selectedAsset] || 0}
                step="0.00000001"
              />
              {selectedAsset && (
                <div className="swap-subtext" style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {quantity && parseFloat(quantity) > 0 && assets.length > 0 && (
                    <span>{t('swap.estimatedSwap')}: {(
                      parseFloat(quantity) * 
                      parseFloat(assets.find(a => a.name === selectedAsset)?.price || 0)
                    ).toFixed(8)} USDT</span>
                  )}
                  {myAssetBalances[selectedAsset] !== undefined && (
                    <span style={{ color: parseFloat(quantity) > (myAssetBalances[selectedAsset] || 0) ? '#ef4444' : 'inherit' }}>
                      {t('swap.maxSwap')}: {(myAssetBalances[selectedAsset] || 0).toFixed(8)} {selectedAsset}
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              className="swap-submit"
              onClick={handleSwap}
              disabled={loading || !selectedAsset || !quantity}
            >
              {loading ? t('swap.swapping') : t('swap.swapButton')}
            </button>
          </div>
        )}

        <div className="swap-table-header">
          <div className="swap-th"><FaCube className="icon" /> {t('swap.asset')}</div>
          <div className="swap-th"><FaHashtag className="icon" /> {t('swap.quantity')}</div>
          <div className="swap-th">{t('swap.target')}</div>
          <div className="swap-th">{t('swap.status')}</div>
          <div className="swap-th right">{t('swap.time')}</div>
        </div>

        {records.length === 0 ? (
          <div className="swap-empty">
            <div className="swap-empty-illustration"><FaExchangeAlt /></div>
            <p>{t('swap.noData')}</p>
          </div>
        ) : (
          <div className="swap-table-body">
            {records.map((record) => {
              const getStatusText = (status) => {
                switch(status) {
                  case 0: return { text: 'Pending', color: '#fbbf24' };
                  case 1: return { text: 'Completed', color: '#10b981' };
                  case 2: return { text: 'Cancelled', color: '#ef4444' };
                  default: return { text: 'Unknown', color: '#6b7280' };
                }
              };
              const statusInfo = getStatusText(record.status);
              const formatTime = (timeStr) => {
                if (!timeStr) return '-';
                const date = new Date(timeStr);
                return date.toLocaleString('zh-CN', { 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                });
              };
              
              return (
                <div key={record.id} className="swap-row">
                  <div className="swap-field" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AssetIcon assetName={record.assetName} size={20} />
                    <span>{record.assetName}</span>
                  </div>
                  <div className="swap-field">{record.quantity || '-'}</div>
                  <div className="swap-field">
                    {record.targetAmount ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600' }}>
                          {record.targetAmount} {record.targetAsset || 'USDT'}
                        </span>
                        {record.exchangeRate && (
                          <span style={{ fontSize: '10px', color: 'rgba(210,220,255,0.6)' }}>
                            @{record.exchangeRate}
                          </span>
                        )}
                      </div>
                    ) : '-'}
                  </div>
                  <div className="swap-field">
                    <span style={{ 
                      color: statusInfo.color, 
                      fontSize: '12px', 
                      fontWeight: '600' 
                    }}>
                      {statusInfo.text}
                    </span>
                  </div>
                  <div className="swap-field right" style={{ fontSize: '11px', color: 'rgba(210,220,255,0.7)' }}>
                    {formatTime(record.createTime)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <BottomNavigation activeTab="swap" onTabChange={onTabChange} />
    </div>
  );
};

export default Swap;
